import { useMemo, useState, type FormEvent } from "react";
import type { PreferredUnits } from "../../athlete-profile/types/athleteProfile";
import { formatWeightFromKilograms } from "../../../lib/unitPreferences";
import {
  TIMELINE_FILTERS,
  displayTimelineProvenance,
  filterTimelineEvents,
  timelineFilterLabel,
  type AthleteTimelineEvent,
  type AthleteTimelineEventType,
  type TimelineFilter
} from "../athleteTimeline";

type ManualTimelineEventDraft = {
  id: string | null;
  eventType: AthleteTimelineEventType;
  eventDate: string;
  title: string;
  summary: string;
  notes: string;
  tags: string;
  metrics: string;
  bodyWeightValue: string;
  bodyWeightUnit: "lb" | "kg";
};

type TimelineSubmitResult = {
  ok: boolean;
  message: string;
  event?: AthleteTimelineEvent;
  diagnostics?: {
    generatedEventId: string | null;
    generatedStableSourceKey: string | null;
    timelineCountBeforeSave: number | null;
    timelineCountAfterSave: number | null;
    lastPersistenceError: string | null;
  };
};

type AthleteTimelinePanelProps = {
  events: AthleteTimelineEvent[];
  onOpenRelatedActivity: (sourceId: string) => void;
  onOpenDailyHealthDate?: (localDate: string) => void;
  onSaveManualEvent: (input: {
    id?: string;
    eventType: AthleteTimelineEventType;
    eventDate: string;
    title: string;
    summary: string;
    notes?: string;
    tags: string[];
    metrics?: Record<string, number>;
  }) => Promise<TimelineSubmitResult>;
  onDeleteEvent: (id: string) => void;
  preferredUnits?: PreferredUnits;
  diagnostics?: {
    totalEvents: number;
    automaticEventsCreated: number;
    duplicateEventsPrevented: number;
    latestEventDate: string | null;
    saveResult: string;
    restoreResult: string;
    sourceLinkageStatus: string;
    lastSubmitAttempted: string | null;
    lastSubmitResult: "idle" | "saving" | "success" | "failed";
    lastValidationError: string | null;
    lastPersistenceError: string | null;
    generatedEventId: string | null;
    generatedStableSourceKey: string | null;
    timelineCountBeforeSave: number | null;
    timelineCountAfterSave: number | null;
    duplicateCandidatesFound: number;
    duplicateEventsReconciled: number;
  };
};

const MANUAL_EVENT_TYPES: AthleteTimelineEventType[] = [
  "body_weight",
  "body_measurement",
  "nutrition_block_start",
  "nutrition_block_end",
  "supplement_change",
  "medication_change",
  "recovery_protocol",
  "injury_or_symptom",
  "bloodwork",
  "equipment_change",
  "athlete_note"
];

function todayLocalDateKey(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function initialDraft(): ManualTimelineEventDraft {
  return {
    id: null,
    eventType: "athlete_note",
    eventDate: todayLocalDateKey(),
    title: "",
    summary: "",
    notes: "",
    tags: "",
    metrics: "",
    bodyWeightValue: "",
    bodyWeightUnit: "lb"
  };
}

function parseMetricInputDetailed(input: string): { metrics: Record<string, number>; errors: string[] } {
  const rows = input.split("\n");
  const parsed: Record<string, number> = {};
  const errors: string[] = [];

  for (const row of rows) {
    const trimmedRow = row.trim();
    if (!trimmedRow) continue;

    const splitIndex = trimmedRow.indexOf(":");
    if (splitIndex <= 0 || splitIndex === trimmedRow.length - 1 || trimmedRow.indexOf(":", splitIndex + 1) >= 0) {
      errors.push(`Invalid metric row: \"${trimmedRow}\". Use key:value format.`);
      continue;
    }

    const key = trimmedRow.slice(0, splitIndex).trim();
    const valueText = trimmedRow.slice(splitIndex + 1).trim();
    const value = Number(valueText);
    if (!key) {
      errors.push(`Invalid metric row: \"${trimmedRow}\". Key is required.`);
      continue;
    }
    if (!Number.isFinite(value)) {
      errors.push(`Invalid metric row: \"${trimmedRow}\". Value must be numeric.`);
      continue;
    }

    parsed[key] = value;
  }

  return { metrics: parsed, errors };
}

function formatEventType(eventType: AthleteTimelineEventType): string {
  return eventType.replaceAll("_", " ");
}

function runtimeEventType(event: AthleteTimelineEvent): AthleteTimelineEventType {
  return event.eventType ?? event.type ?? "manual_note";
}

function formatDateLabel(dateKey: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) return dateKey;

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, monthIndex, day, 12, 0, 0);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function bodyWeightMetricLabel(metrics: Record<string, number> | undefined, preferredUnits: PreferredUnits): string | null {
  if (!metrics) return null;
  if (typeof metrics.weight_kg === "number") return formatWeightFromKilograms(metrics.weight_kg, preferredUnits);
  if (typeof metrics.weight_lb === "number") return formatWeightFromKilograms(metrics.weight_lb * 0.45359237, preferredUnits);
  return null;
}

export function AthleteTimelinePanel({ events, onOpenRelatedActivity, onOpenDailyHealthDate, onSaveManualEvent, onDeleteEvent, preferredUnits = "imperial", diagnostics }: AthleteTimelinePanelProps) {
  const [selectedFilter, setSelectedFilter] = useState<TimelineFilter>("all");
  const [draft, setDraft] = useState<ManualTimelineEventDraft>(initialDraft);
  const [isSaving, setIsSaving] = useState(false);
  const [submitFeedback, setSubmitFeedback] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [persistenceError, setPersistenceError] = useState<string | null>(null);
  const [newEventVisibleUnderFilter, setNewEventVisibleUnderFilter] = useState<boolean | null>(null);

  const filteredEvents = useMemo(() => filterTimelineEvents(events, selectedFilter), [events, selectedFilter]);
  const groupedEvents = useMemo(() => {
    const groups = new Map<string, AthleteTimelineEvent[]>();
    for (const event of filteredEvents) {
      const key = event.eventDate;
      const bucket = groups.get(key) ?? [];
      bucket.push(event);
      groups.set(key, bucket);
    }
    return [...groups.entries()].sort((left, right) => Date.parse(right[0]) - Date.parse(left[0]));
  }, [filteredEvents]);

  const metricsPlaceholder = draft.eventType === "body_measurement"
    ? "waist_cm: 83.0\ncalf_cm: 37.0\nquad_cm: 57.0"
    : "weight_lb: 168.2\nwaist_cm: 83.0";

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setSubmitFeedback(null);
    setValidationError(null);
    setPersistenceError(null);

    if (!draft.eventDate || !draft.title.trim() || !draft.summary.trim()) {
      setValidationError("Date, title, and summary are required.");
      return;
    }

    const metricParse = parseMetricInputDetailed(draft.metrics);
    if (metricParse.errors.length > 0) {
      setValidationError(metricParse.errors[0]);
      return;
    }

    const metrics: Record<string, number> = { ...metricParse.metrics };

    if (draft.eventType === "body_weight") {
      const numericWeight = Number(draft.bodyWeightValue.trim());
      if (!Number.isFinite(numericWeight) || numericWeight <= 0) {
        setValidationError("Body weight requires a positive numeric value.");
        return;
      }
      metrics[draft.bodyWeightUnit === "kg" ? "weight_kg" : "weight_lb"] = numericWeight;
    }

    setIsSaving(true);
    try {
      const result = await onSaveManualEvent({
        id: draft.id ?? undefined,
        eventType: draft.eventType,
        eventDate: draft.eventDate,
        title: draft.title.trim(),
        summary: draft.summary.trim(),
        notes: draft.notes.trim() || undefined,
        tags: draft.tags.split(",").map(tag => tag.trim()).filter(Boolean),
        metrics: Object.keys(metrics).length > 0 ? metrics : undefined
      });

      if (!result.ok || !result.event) {
        setPersistenceError(result.message || "Unable to save timeline event.");
        return;
      }

      const visibleUnderCurrentFilter = filterTimelineEvents([result.event], selectedFilter).length > 0;
      setNewEventVisibleUnderFilter(visibleUnderCurrentFilter);
      if (!visibleUnderCurrentFilter) {
        setSelectedFilter("all");
        setSubmitFeedback("Timeline event added. Switched to All so you can see it.");
      } else {
        setSubmitFeedback(draft.id ? "Timeline event updated" : "Timeline event added");
      }

      setDraft(initialDraft());
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : "Unable to save timeline event.";
      setPersistenceError(message);
    } finally {
      setIsSaving(false);
    }
  }

  function startEditingEvent(event: AthleteTimelineEvent) {
    if (event.source !== "manual_entry") {
      return;
    }

    const eventType = runtimeEventType(event);

    const metrics = event.metrics
      ? Object.entries(event.metrics).map(([key, value]) => `${key}: ${value}`).join("\n")
      : "";

    const bodyWeightLb = event.metrics?.weight_lb;
    const bodyWeightKg = event.metrics?.weight_kg;
    setDraft({
      id: event.id,
      eventType,
      eventDate: event.eventDate,
      title: event.title,
      summary: event.summary,
      notes: event.notes ?? "",
      tags: event.tags.join(", "),
      metrics,
      bodyWeightValue: typeof bodyWeightLb === "number"
        ? String(bodyWeightLb)
        : typeof bodyWeightKg === "number"
          ? String(bodyWeightKg)
          : "",
      bodyWeightUnit: typeof bodyWeightKg === "number" ? "kg" : "lb"
    });
    setSubmitFeedback(null);
    setValidationError(null);
    setPersistenceError(null);
  }

  return (
    <section className="athlete-timeline-panel" aria-labelledby="athlete-timeline-title">
      <div className="athlete-timeline-header">
        <div>
          <p className="eyebrow">ATHLETE TIMELINE</p>
          <h2 id="athlete-timeline-title">Athlete Timeline</h2>
          <p className="command-note">Chronological context from completed activities, profile updates, and manual events.</p>
        </div>
        <span className="coach-summary-badge">{events.length} events</span>
      </div>

      <div className="timeline-filter-row" role="tablist" aria-label="Timeline filters">
        {TIMELINE_FILTERS.map(filter => (
          <button
            key={filter}
            type="button"
            role="tab"
            aria-selected={selectedFilter === filter}
            className={`timeline-filter-button ${selectedFilter === filter ? "active" : ""}`}
            onClick={() => setSelectedFilter(filter)}
          >
            {timelineFilterLabel(filter)}
          </button>
        ))}
      </div>

      <details className="timeline-manual-form">
        <summary>{draft.id ? "Edit timeline event" : "Add timeline event"}</summary>
        <form className="timeline-form-grid" onSubmit={handleSubmit}>
          <label>Event type
            <select
              value={draft.eventType}
              onChange={event => {
                const nextType = event.target.value as AthleteTimelineEventType;
                setDraft(current => ({
                  ...current,
                  eventType: nextType,
                  title: nextType === "body_weight" && !current.title.trim() ? "Body weight recorded" : current.title
                }));
              }}
            >
              {MANUAL_EVENT_TYPES.map(eventType => (
                <option key={eventType} value={eventType}>{formatEventType(eventType)}</option>
              ))}
            </select>
          </label>
          <label>Date
            <input
              type="date"
              value={draft.eventDate}
              onChange={event => setDraft(current => ({ ...current, eventDate: event.target.value }))}
            />
          </label>

          {draft.eventType === "body_weight" ? (
            <>
              <label>Body weight value
                <input
                  type="number"
                  step="0.1"
                  value={draft.bodyWeightValue}
                  onChange={event => setDraft(current => ({ ...current, bodyWeightValue: event.target.value }))}
                  placeholder="168.2"
                />
              </label>
              <label>Body weight unit
                <select
                  value={draft.bodyWeightUnit}
                  onChange={event => setDraft(current => ({ ...current, bodyWeightUnit: event.target.value as "lb" | "kg" }))}
                >
                  <option value="lb">lb</option>
                  <option value="kg">kg</option>
                </select>
              </label>
            </>
          ) : null}

          <label className="timeline-form-span-2">Title
            <input
              value={draft.title}
              onChange={event => setDraft(current => ({ ...current, title: event.target.value }))}
              placeholder={draft.eventType === "body_weight" ? "Body weight recorded" : "Athlete note"}
            />
          </label>
          <label className="timeline-form-span-2">Summary
            <input
              value={draft.summary}
              onChange={event => setDraft(current => ({ ...current, summary: event.target.value }))}
              placeholder={draft.eventType === "athlete_note" ? "Context note" : "Morning measurement"}
            />
          </label>
          <label className="timeline-form-span-2">Notes (optional)
            <textarea
              rows={2}
              value={draft.notes}
              onChange={event => setDraft(current => ({ ...current, notes: event.target.value }))}
            />
          </label>
          <label>Tags (comma-separated)
            <input
              value={draft.tags}
              onChange={event => setDraft(current => ({ ...current, tags: event.target.value }))}
              placeholder="body, weekly check"
            />
          </label>
          <label>Numeric metrics (key:value per line)
            <textarea
              rows={3}
              value={draft.metrics}
              onChange={event => setDraft(current => ({ ...current, metrics: event.target.value }))}
              placeholder={metricsPlaceholder}
            />
          </label>

          <div className="timeline-form-span-2 timeline-form-actions">
            <button type="submit" className="ride-analysis-button primary-action" disabled={isSaving}>
              {isSaving ? "Adding..." : (draft.id ? "Save Event" : "Add Event")}
            </button>
            {draft.id ? (
              <button type="button" className="ride-analysis-button secondary-action" onClick={() => setDraft(initialDraft())}>Cancel Edit</button>
            ) : null}
          </div>

          {submitFeedback ? <p className="timeline-submit-success" role="status">{submitFeedback}</p> : null}
          {validationError ? <p className="timeline-submit-error" role="alert">{validationError}</p> : null}
          {persistenceError ? <p className="timeline-submit-error" role="alert">{persistenceError}</p> : null}
        </form>
      </details>

      {groupedEvents.length === 0 ? (
        <p className="command-note">No timeline events match this filter.</p>
      ) : (
        <div className="timeline-event-groups">
          {groupedEvents.map(([date, dayEvents]) => (
            <section key={date} className="timeline-day-group">
              <h3>{formatDateLabel(date)}</h3>
              <ul className="timeline-event-list">
                {dayEvents.map(event => (
                  <li key={event.id} className="timeline-event-item">
                    <div>
                      <p className="timeline-event-type">{formatEventType(runtimeEventType(event))}</p>
                      <strong>{event.title}</strong>
                      <p className="command-note">{event.summary}</p>
                      {runtimeEventType(event) === "body_weight" ? (
                        <p className="command-note">Body weight: {bodyWeightMetricLabel(event.metrics, preferredUnits) ?? "Not provided"}</p>
                      ) : null}
                      <p className="command-note">
                        Source: {displayTimelineProvenance(event.source)}
                        {event.confidence ? ` · Confidence: ${event.confidence}` : ""}
                      </p>
                    </div>
                    <div className="timeline-event-actions">
                      {event.source === "automatic_completed_activity" && event.sourceId ? (
                        <button
                          type="button"
                          className="ride-analysis-button secondary-action"
                          onClick={() => {
                            const relatedSourceId = event.sourceId;
                            if (!relatedSourceId) return;
                            onOpenRelatedActivity(relatedSourceId.replace(/^race:/, "").replace(/^activity_completed:/, "").replace(":race", ""));
                          }}
                        >
                          Open Related Activity
                        </button>
                      ) : null}
                      {event.source === "imported_source" && event.tags.includes("daily_health") && onOpenDailyHealthDate ? (
                        <button
                          type="button"
                          className="ride-analysis-button secondary-action"
                          onClick={() => onOpenDailyHealthDate(event.eventDate)}
                        >
                          Open Daily Health
                        </button>
                      ) : null}
                      {event.source === "manual_entry" ? (
                        <button type="button" className="ride-analysis-button secondary-action" onClick={() => startEditingEvent(event)}>Edit</button>
                      ) : null}
                      <button
                        type="button"
                        className="ride-analysis-button secondary-action"
                        onClick={() => {
                          if (window.confirm("Delete this timeline event?")) {
                            onDeleteEvent(event.id);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <details className="upload-diagnostics">
        <summary>Timeline diagnostics</summary>
        <dl>
          <div><dt>Total timeline events</dt><dd>{diagnostics?.totalEvents ?? events.length}</dd></div>
          <div><dt>Automatic events created</dt><dd>{diagnostics?.automaticEventsCreated ?? 0}</dd></div>
          <div><dt>Duplicate events prevented</dt><dd>{diagnostics?.duplicateEventsPrevented ?? 0}</dd></div>
          <div><dt>Latest event date</dt><dd>{diagnostics?.latestEventDate ?? "None"}</dd></div>
          <div><dt>Persistence save result</dt><dd>{diagnostics?.saveResult ?? "idle"}</dd></div>
          <div><dt>Restore result</dt><dd>{diagnostics?.restoreResult ?? "idle"}</dd></div>
          <div><dt>Source linkage status</dt><dd>{diagnostics?.sourceLinkageStatus ?? "unknown"}</dd></div>
          <div><dt>Last submit attempted</dt><dd>{diagnostics?.lastSubmitAttempted ?? "None"}</dd></div>
          <div><dt>Last submit result</dt><dd>{diagnostics?.lastSubmitResult ?? "idle"}</dd></div>
          <div><dt>Last validation error</dt><dd>{diagnostics?.lastValidationError ?? validationError ?? "None"}</dd></div>
          <div><dt>Last persistence error</dt><dd>{diagnostics?.lastPersistenceError ?? persistenceError ?? "None"}</dd></div>
          <div><dt>Generated event ID</dt><dd>{diagnostics?.generatedEventId ?? "None"}</dd></div>
          <div><dt>Generated stable source key</dt><dd>{diagnostics?.generatedStableSourceKey ?? "None"}</dd></div>
          <div><dt>Timeline count before save</dt><dd>{diagnostics?.timelineCountBeforeSave ?? "None"}</dd></div>
          <div><dt>Timeline count after save</dt><dd>{diagnostics?.timelineCountAfterSave ?? "None"}</dd></div>
          <div><dt>Duplicate candidates found</dt><dd>{diagnostics?.duplicateCandidatesFound ?? 0}</dd></div>
          <div><dt>Duplicate events reconciled</dt><dd>{diagnostics?.duplicateEventsReconciled ?? 0}</dd></div>
          <div><dt>Active filter</dt><dd>{timelineFilterLabel(selectedFilter)}</dd></div>
          <div><dt>New event visible under active filter</dt><dd>{newEventVisibleUnderFilter === null ? "Unknown" : (newEventVisibleUnderFilter ? "Yes" : "No")}</dd></div>
        </dl>
      </details>
    </section>
  );
}
